import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { IProject, IUserSummary } from '../../types/global/helper';
import { animated } from '@react-spring/web';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useEsHover } from '../Animation/useEsHover';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import { convertMinuteSecondsDays, formatNumberWithK } from '../../utils/AppUtils';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import { HamburgerIcon } from '@chakra-ui/icons';
import { FullScreenMoreMenu } from '../More/FullScreenMoreMenu';
import { RichTextViewer } from '../Common/RichTextViewer';

export interface IEditorialRecommendationThumbnail {
    project: IProject;
    onClick?: () => void;
    isPlayingIconEnabled?: boolean;
    disable?: boolean;
    dragableMode?: boolean;
    isEditMode?: boolean;
    isFirstItemInList?: boolean;
    isLastItemInList?: boolean;
    onRemovePlaylistItem?: (project: IProject) => void;
    onAdjustPlaylistPosition?: (position: 'up' | 'down') => void;
}

function formatVideoDuration(seconds?: number): string | null {
    if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return null;
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Stacked “editorial” recommendation card (thumbnail on top, creator row below) — matches Watch recommendations mock.
 */
export const EditorialRecommendationThumbnail: React.FC<IEditorialRecommendationThumbnail> = ({
    project,
    onClick,
    isPlayingIconEnabled,
    disable,
    dragableMode,
    isEditMode,
    isFirstItemInList,
    isLastItemInList,
    onRemovePlaylistItem,
    onAdjustPlaylistPosition,
}) => {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const { handleMouseEnter, handleMouseLeave, isHover } = useEsHover();

    const handleClick = () => {
        if (disable) return;
        onClick?.();
    };

    const menu = [
        { id: 'remove', title: 'Remove from playlist' },
        ...(!isLastItemInList ? [{ id: 'down', title: 'Move down' }] : []),
        ...(!isFirstItemInList ? [{ id: 'up', title: 'Move Up' }] : []),
    ];

    const handleMoreItemClick = (moreType: string) => {
        switch (moreType) {
            case 'up':
            case 'down':
                onAdjustPlaylistPosition?.(moreType);
                break;
            case 'remove':
                onRemovePlaylistItem?.(project);
                break;
        }
    };

    const durationLabel = formatVideoDuration(project.metadata?.duration);
    const thumb = project.thumbnail ?? project.feature_image;

    return (
        <animated.div
            style={{ cursor: disable ? 'default' : 'pointer', position: 'relative', width: '100%' }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}>
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    mb: 2.5,
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: 'rgba(26, 25, 25, 0.35)',
                    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.45)',
                    transition: 'box-shadow 0.35s ease',
                    '&:hover': { boxShadow: '0 28px 55px -10px rgba(0,0,0,0.55)' },
                }}>
                <Stack direction="row" sx={{ alignItems: 'stretch' }}>
                    {dragableMode && (
                        <Stack justifyContent="center" alignItems="center" px={0.5} sx={{ flexShrink: 0 }}>
                            <HamburgerIcon sx={{ h: '40px' }} color={t.tag_white} />
                        </Stack>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                            onClick={handleClick}
                            sx={{
                                position: 'relative',
                                width: '100%',
                                pt: '56.25%',
                                borderRadius: 2,
                                overflow: 'hidden',
                                bgcolor: t.zeus_dark,
                            }}>
                            {thumb && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        '& img': {
                                            width: '100%',
                                            height: '100%',
                                            objectFit: isHover ? 'contain' : 'cover',
                                            transition: 'transform 1s ease, object-fit 0.2s',
                                            ...(isHover ? {} : {}),
                                        },
                                    }}>
                                    <CustomLazyLoadImage
                                        src={thumb}
                                        alt=""
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: isHover ? 'contain' : 'cover',
                                        }}
                                    />
                                </Box>
                            )}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    bgcolor: isHover ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.2)',
                                    pointerEvents: 'none',
                                    transition: 'background-color 0.3s',
                                }}
                            />
                            {isPlayingIconEnabled && (
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.75}
                                    sx={{
                                        position: 'absolute',
                                        top: 12,
                                        left: 12,
                                        px: 1.25,
                                        py: 0.5,
                                        borderRadius: 999,
                                        bgcolor: 'rgba(0, 253, 193, 0.92)',
                                        backdropFilter: 'blur(8px)',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                                    }}>
                                    <Box
                                        sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            bgcolor: '#004734',
                                            animation: 'pulse 1.5s ease-in-out infinite',
                                            '@keyframes pulse': {
                                                '0%, 100%': { opacity: 1 },
                                                '50%': { opacity: 0.35 },
                                            },
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 10,
                                            letterSpacing: '0.12em',
                                            color: '#004734',
                                            textTransform: 'uppercase',
                                        }}>
                                        Playing
                                    </Typography>
                                </Stack>
                            )}
                            {durationLabel && (
                                <Typography
                                    sx={{
                                        position: 'absolute',
                                        bottom: 12,
                                        right: 12,
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        fontSize: 12,
                                        fontFamily: 'monospace',
                                        color: t.lightWhite,
                                        bgcolor: 'rgba(14,14,14,0.82)',
                                        border: '1px solid rgba(255,255,255,0.06)',
                                    }}>
                                    {durationLabel}
                                </Typography>
                            )}
                        </Box>

                        <Stack direction="row" spacing={2.5} sx={{ py: 2, px: 1, alignItems: 'flex-start' }}>
                            {project.created_by && (
                                <ThspianAvatar
                                    user={project.created_by as unknown as IUserSummary}
                                    size="large"
                                    badgeSx={{
                                        border: `1px solid ${t.highlight}33`,
                                        borderRadius: '50%',
                                    }}
                                />
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }} onClick={handleClick}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontWeight: 800,
                                        fontSize: { xs: 18, sm: 20 },
                                        lineHeight: 1.25,
                                        color: t.lightWhite,
                                        mb: 1,
                                        display: '-webkit-box',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 2,
                                        overflow: 'hidden',
                                    }}>
                                    {project.title}
                                </Typography>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    flexWrap="wrap"
                                    spacing={0.75}
                                    sx={{ mb: 1.5, rowGap: 0.5 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: 13,
                                            fontWeight: 600,
                                            color: 'rgba(255,255,255,0.88)',
                                        }}>
                                        {project.created_by?.firstName} {project.created_by?.lastName}
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: 5,
                                            height: 5,
                                            borderRadius: '50%',
                                            bgcolor: 'rgba(118,117,117,0.35)',
                                        }}
                                    />
                                    <Typography sx={{ fontSize: 13, color: t.grey }}>
                                        {formatNumberWithK(project.view_count ?? 0)} view
                                        {(project.view_count ?? 0) !== 1 ? 's' : ''}
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: 5,
                                            height: 5,
                                            borderRadius: '50%',
                                            bgcolor: 'rgba(118,117,117,0.35)',
                                        }}
                                    />
                                    <Typography sx={{ fontSize: 13, color: t.grey }}>
                                        {convertMinuteSecondsDays(project.createdAt)} ago
                                    </Typography>
                                </Stack>
                                {project.description ? (
                                    <RichTextViewer
                                        content={project.description}
                                        textStyle={{
                                            padding: 0,
                                            display: '-webkit-box',
                                            overflow: 'hidden',
                                            WebkitBoxOrient: 'vertical',
                                            WebkitLineClamp: 3,
                                            lineHeight: 1.55,
                                            maxWidth: '100%',
                                            whiteSpace: 'normal',
                                            fontSize: 14,
                                            color: t.grey,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        }}
                                    />
                                ) : null}
                            </Box>
                        </Stack>
                    </Box>
                </Stack>
                {isEditMode && (
                    <FullScreenMoreMenu
                        menu={menu}
                        onClick={handleMoreItemClick}
                        title=""
                        customIconSx={{
                            top: 8,
                            right: 8,
                            position: 'absolute',
                            zIndex: 12,
                        }}
                    />
                )}
            </Box>
        </animated.div>
    );
};
